/**
 * RECALC INDICATOR - Indicador de Recálculo
 * 
 * Mostra feedback visual do estado de recálculo do valuation:
 * - 🔄 "Recalculando..." (spinner) quando loading === true
 * - ✅ "Atualizado há Xs" (checkmark) quando loading === false
 * 
 * Uso: <RecalcIndicator />
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Check as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import { RootState } from '../store';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface RecalcIndicatorProps {
  /** Variante do componente (compacto ou completo) */
  variant?: 'compact' | 'full';
}

export const RecalcIndicator: React.FC<RecalcIndicatorProps> = ({ 
  variant = 'compact'
}) => {
  const loading = useSelector((state: RootState) => state.valuation.loading);
  const error = useSelector((state: RootState) => state.valuation.error);
  const lastUpdated = useSelector((state: RootState) => state.valuation.lastUpdated);
  
  // Estado para forçar re-render a cada 5 segundos (atualizar "há X tempo")
  const [, setTick] = useState(0);
  
  useEffect(() => {
    if (!loading && lastUpdated) {
      const interval = setInterval(() => {
        setTick(prev => prev + 1);
      }, 5000); // Atualiza a cada 5 segundos
      
      return () => clearInterval(interval);
    }
  }, [loading, lastUpdated]);
  
  // ============================================================================
  // ESTADOS VISUAIS
  // ============================================================================
  
  if (loading) {
    // Estado: RECALCULANDO
    return (
      <Tooltip title="O valuation está sendo recalculado automaticamente">
        <Chip
          icon={<CircularProgress size={16} color="inherit" />}
          label={variant === 'full' ? 'Recalculando...' : 'Calculando'}
          color="warning"
          size="small"
          sx={{ 
            fontWeight: 500,
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 }
            }
          }}
        />
      </Tooltip>
    );
  }
  
  if (error) {
    // Estado: ERRO
    return (
      <Tooltip title={`Erro: ${error}`}>
        <Chip
          icon={<ErrorIcon />}
          label={variant === 'full' ? 'Erro no cálculo' : 'Erro'}
          color="error"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </Tooltip>
    );
  }
  
  if (lastUpdated) {
    // Estado: ATUALIZADO
    const timeAgo = formatDistanceToNow(new Date(lastUpdated), { 
      addSuffix: true, 
      locale: ptBR 
    });
    
    return (
      <Tooltip title={`Última atualização: ${new Date(lastUpdated).toLocaleString('pt-BR')}`}>
        <Chip
          icon={<CheckIcon />}
          label={variant === 'full' ? `Atualizado ${timeAgo}` : 'Atualizado'}
          color="success"
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </Tooltip>
    );
  }
  
  // Estado: AGUARDANDO PRIMEIRA EXECUÇÃO
  return (
    <Tooltip title="Aguardando primeiro cálculo">
      <Chip
        label="Aguardando"
        color="default"
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    </Tooltip>
  );
};

/**
 * Variante em linha (inline) para usar em textos
 */
export const RecalcIndicatorInline: React.FC = () => {
  const loading = useSelector((state: RootState) => state.valuation.loading);
  const error = useSelector((state: RootState) => state.valuation.error);
  const lastUpdated = useSelector((state: RootState) => state.valuation.lastUpdated);
  
  if (loading) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={12} color="inherit" />
        <Typography variant="caption" color="text.secondary">
          recalculando...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Typography variant="caption" color="error">
        (erro no cálculo)
      </Typography>
    );
  }
  
  if (lastUpdated) {
    const timeAgo = formatDistanceToNow(new Date(lastUpdated), { 
      addSuffix: true, 
      locale: ptBR 
    });
    
    return (
      <Typography variant="caption" color="success.main">
        (atualizado {timeAgo})
      </Typography>
    );
  }
  
  return null;
};

export default RecalcIndicator;
